// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Button,
  DataTableSkeleton,
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Add } from '@carbon/icons-react';
import { useOrganizations, describeApiError } from '../../shared/provisioning';
import styles from './OrganizationsPage.module.css';

// ponytail: no site count yet — it needs a listSites call per org (N+1).
// Add it when the column earns its keep, or when the BFF returns a count.
export default function OrganizationsPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useOrganizations();

  return (
    <>
      <div className={styles.head}>
        <h1 className={styles.title}>Organizations</h1>
        <span className={styles.spacer} />
        <Button renderIcon={Add} size="sm" onClick={() => navigate('/organizations/new')}>
          New organization
        </Button>
      </div>

      {isLoading && <DataTableSkeleton columnCount={4} rowCount={5} showHeader={false} />}

      {isError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Could not load organizations"
          subtitle={describeApiError(error, { action: 'view organizations' }).message}
          hideCloseButton
        />
      )}

      {!isLoading && !isError && data?.length === 0 && (
        <div className={styles.empty}>No organizations yet.</div>
      )}

      {!isLoading && !isError && !!data?.length && (
        <Table size="sm" useZebraStyles>
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Key</TableHeader>
              <TableHeader>Segment</TableHeader>
              <TableHeader>Time zone</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map(org => (
              <TableRow key={org.id}>
                <TableCell>
                  <Link to={`/organizations/${org.id}/sites`}>{org.preferredName}</Link>
                </TableCell>
                <TableCell>{org.organizationKey}</TableCell>
                <TableCell>{org.primaryBusinessSegment}</TableCell>
                <TableCell>{org.timeZone}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}
